import { AutomationActionType } from '@dadohr/shared/domainTypes/experience';
import {
  HoveredEvents,
  PAEventType
} from '@dadohr/shared/domainTypes/productAnalytics';
import {
  IReportOverviewAutomations,
  SimpleStatusCounts
} from '@dadohr/shared/domainTypes/reports';
import styled from '@emotion/styled';
import { Grid, Typography } from '@material-ui/core';
import { useUnsafeTranslation } from '../../../../../i18next';
import { FlexContainer } from '../../../../../layout/Flex';
import { useProductAnalytics } from '../../../../../services/productAnalytics';
import { useTheme } from '../../../../../themes';
import { useActionDefinitions } from '../../../../Experiences/components/Builders/AutomationBuilder/actionHelpers';

type Props = {
  automationType: AutomationActionType;
  number: number;
};

type TrackingInfo = {
  name: HoveredEvents;
  explanation: string;
};

const NumberContainer = styled('div')`
  display: flex;
  align-items: flex-end;
  font-size: 2rem;
  font-weight: ${(p) => p.theme.typography.fontWeightLight};
  padding-top: 8px;
`;

const GroupContainer = styled('div')`
  display: flex;
  align-items: flex-end;
  font-weight: ${(p) => p.theme.typography.fontWeightLight};
`;

const BigNumContainer = styled('span')`
  font-size: 2rem;
  font-weight: ${(p) => p.theme.typography.fontWeightLight};
`;

const ReportBigNumberAutomationItem: React.FC<Props> = ({
  automationType,
  number
}) => {
  const defs = useActionDefinitions();
  const def = defs.find((d) => d.type === automationType);
  const theme = useTheme();

  return (
    <>
      <FlexContainer
        justifyContent="flex-start"
        direction="column"
        alignItems="flex-start"
      >
        <GroupContainer>
          <img src={def?.iconSrc} width="20" alt="integration tool icon" />
          <Typography
            component="div"
            variant="body1"
            color="textPrimary"
            style={{
              fontSize: '1.18rem',
              paddingLeft: '6px',
              fontWeight: theme.typography.fontWeightLight
            }}
          >
            {def?.shortLabel}
          </Typography>
        </GroupContainer>

        {!!number && <NumberContainer>{number}</NumberContainer>}
      </FlexContainer>
    </>
  );
};

const getRelevantTotal = (x: SimpleStatusCounts) => x.done + x.error;

export const ReportBigNumberAutomation = ({
  data,
  title
}: {
  data: IReportOverviewAutomations;
  title?: string;
}) => {
  const { t } = useUnsafeTranslation();
  const { trackEvent } = useProductAnalytics();
  const theme = useTheme();

  // Assuming this component will be used somewhere else in the future
  const stats: Record<string, TrackingInfo> = {
    automations: {
      name: 'Automations Executed Statistics Hovered',
      explanation:
        'Admin user hovered over the totals for automations and types of actions triggered'
    }
  };
  return (
    <>
      <FlexContainer
        justifyContent="flex-start"
        direction="column"
        alignItems="flex-start"
        marginBottom={6}
        onMouseEnter={() =>
          Object.keys(stats).forEach((stat: string) => {
            if (stat === title) {
              trackEvent({
                eventType: PAEventType.OTHER,
                name: stats[stat].name,
                Explanation: stats[stat].explanation
              });
            }
          })
        }
      >
        <Grid container direction="column" justify="space-between" spacing={1}>
          <Grid item>
            <Typography
              component="div"
              variant="body2"
              color="textSecondary"
              style={{
                fontSize: '0.8rem',
                fontWeight: theme.typography.fontWeightBold,
                textTransform: 'uppercase'
              }}
            >
              {t('Actions Executed')}
            </Typography>
          </Grid>

          <Grid item>
            <BigNumContainer>{getRelevantTotal(data.counts)}</BigNumContainer>
          </Grid>
        </Grid>
      </FlexContainer>
      <FlexContainer
        justifyContent="flex-start"
        wrap="wrap"
        direction="column"
        alignItems="flex-start"
        spacing={2}
      >
        {data.counts.total > 0 && (
          <Typography
            component="div"
            variant="body1"
            color="textPrimary"
            style={{
              fontSize: '1.18rem',
              fontWeight: theme.typography.fontWeightBold
            }}
          >
            {t('Types of actions executed')}
          </Typography>
        )}
        <Grid container spacing={10}>
          {Object.values(data.byType).map((item) => {
            const total = getRelevantTotal(item.counts);
            if (total === 0) {
              return null;
            }
            return (
              <Grid item key={item.type}>
                <ReportBigNumberAutomationItem
                  automationType={item.type}
                  number={total}
                />
              </Grid>
            );
          })}
        </Grid>
      </FlexContainer>
    </>
  );
};
